"use client"

import type { LucideProps } from "lucide-react"
import type { AnimationGeneratorType } from "motion/react"
import type { ForwardRefExoticComponent, RefAttributes } from "react"
import type { TNavItems } from "@/models/navbar.model"
import { ChevronRight } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { useTranslations } from "next-intl"
import { parseAsString, useQueryState } from "nuqs"
import React, { useCallback, useMemo } from "react"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useRoutes } from "@/hooks/use-routes"
import { Link, usePathname } from "@/i18n/navigation"
import { cn } from "@/lib/utils"

type LucideIcon = ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>

type FlattenedRoute = {
  key: string
  title: string
  url: string
  icon?: LucideIcon
  description?: string
  items?: TNavItems[]
  isActive?: boolean
}

// Recursive component to handle nested navigation items
function NavItem({
  item,
  level = 0,
  collapse,
  parseTitle,
  pathname,
}: {
  item: TNavItems
  level?: number
  collapse: boolean
  parseTitle: (title: string) => string
  pathname: string
}) {
  const { state } = useSidebar()
  const hasSubItems = item.subItems && item.subItems.length > 0

  if (level === 0) {
    // Top level items - these should be rendered as regular menu items
    // The items field is handled in the parent component
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          className={cn(
            "text-sidebar-foreground no-underline",
            pathname.startsWith(item.url) && "bg-sidebar-accent text-sidebar-accent-foreground",
          )}
          forceTooltip={collapse}
          tooltip={item.title}
        >
          <Link href={item.url}>
            {item.icon && React.createElement(item.icon, { className: "size-4" })}
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }
  else {
    // Nested items (level > 0) - these use subItems
    if (hasSubItems) {
      return (
        <SidebarMenuSubItem key={item.key}>
          <Collapsible className="group/sub-collapsible">
            <div className="flex items-center gap-2">
              {/* Item link - navigates to item page */}
              <SidebarMenuSubButton
                asChild
                className={cn(
                  "flex-1 no-underline h-auto min-h-7",
                  pathname.startsWith(item.url) && "bg-sidebar-accent text-sidebar-accent-foreground",
                )}
              >
                <Link href={item.url}>
                  {item.icon && React.createElement(item.icon, { className: "size-4" })}
                  <span>{parseTitle(item.title)}</span>
                </Link>
              </SidebarMenuSubButton>

              {/* Chevron trigger - expands submenu */}
              <CollapsibleTrigger asChild>
                <SidebarMenuSubButton className="flex h-6 w-6 items-center justify-center p-0 hover:bg-accent">
                  <ChevronRight className="h-3 w-3 transition-transform duration-200 group-data-[state=open]/sub-collapsible:rotate-90" />
                </SidebarMenuSubButton>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <SidebarMenuSub>
                {item.subItems?.map(subItem => (
                  <NavItem
                    key={subItem.key}
                    collapse={collapse}
                    item={subItem}
                    level={level + 1}
                    parseTitle={parseTitle}
                    pathname={pathname}
                  />
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </Collapsible>
        </SidebarMenuSubItem>
      )
    }
    else {
      return (
        <SidebarMenuSubItem key={item.key}>
          <SidebarMenuSubButton
            asChild
            className={cn(
              "no-underline h-auto min-h-7",
              pathname.startsWith(item.url) && "bg-sidebar-accent text-sidebar-accent-foreground",
            )}
          >
            <Link href={item.url}>
              {item.icon && React.createElement(item.icon, { className: "size-4" })}
              <span>{parseTitle(item.title)}</span>
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      )
    }
  }
}

export function NavMain({ collapse }: { collapse: boolean }) {
  const t = useTranslations("navbar.main")
  const routes = useRoutes()
  const [inputSearch] = useQueryState("sidebar-search", parseAsString.withDefault(""))
  const isSearching = inputSearch.length > 0
  const pathname = usePathname()
  const { state } = useSidebar()

  const parseTitle = useCallback((title: string): string => {
    return routes.find(route => route.items?.find(item => item.key === title))?.items?.find(item => item.key === title)?.description || title
  }, [routes])

  const filteredModules = useMemo(() => {
    if (!inputSearch)
      return routes

    const searchTerm = inputSearch.toLowerCase()
    const flattenedResults: FlattenedRoute[] = []

    // Recursive function to flatten nested items
    const flattenItems = (items: TNavItems[], parentIcon?: LucideIcon): FlattenedRoute[] => {
      const results: FlattenedRoute[] = []

      items.forEach((item) => {
        const matchesItem = parseTitle(item.title)?.toLowerCase().includes(searchTerm)
        const matchesSubItems = item.subItems?.some(subItem =>
          parseTitle(subItem.title)?.toLowerCase().includes(searchTerm),
        )

        if (matchesItem) {
          results.push({
            key: item.key,
            title: parseTitle(item.title),
            url: item.url,
            icon: parentIcon,
            description: item.description,
          })
        }

        if (item.subItems) {
          const subResults = flattenItems(item.subItems, parentIcon)
          results.push(...subResults)
        }
      })

      return results
    }

    routes.forEach((platform) => {
      // Check platform title
      if (platform.title.toLowerCase().includes(searchTerm)) {
        flattenedResults.push({
          ...platform,
          items: undefined, // Remove nested items when flattening
        })
      }

      // Check items and subitems recursively
      if (platform.items) {
        const platformResults = flattenItems(platform.items, platform.icon)
        flattenedResults.push(...platformResults)
      }
    })

    return flattenedResults
  }, [inputSearch, parseTitle, routes])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring" as AnimationGeneratorType,
        stiffness: 300,
        damping: 24,
      },
    },
  }

  return (
    <SidebarGroup>
      {!collapse && <SidebarGroupLabel>{isSearching ? t("search_results") : t("modules")}</SidebarGroupLabel>}
      <AnimatePresence mode="wait">
        <motion.div
          key={isSearching ? "search" : "normal"}
          animate="visible"
          exit="hidden"
          initial="hidden"
          variants={containerVariants}
        >
          <SidebarMenu>
            {filteredModules.map(item =>
              isSearching
                ? (
                    <motion.div key={item.key} variants={itemVariants}>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          asChild
                          className={cn(
                            "text-sidebar-foreground no-underline",
                            pathname.startsWith(item.url) && "bg-sidebar-accent text-sidebar-accent-foreground",
                          )}
                          forceTooltip={collapse}
                          tooltip={item.title}
                        >
                          <Link href={item.url}>
                            {item.icon && React.createElement(item.icon, { className: "size-4" })}
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </motion.div>
                  )
                : (
                    <motion.div key={item.key} variants={itemVariants}>
                      {item.items && !collapse
                        ? (
                            <Collapsible className="group/collapsible" defaultOpen={false}>
                              <SidebarMenuItem>
                                <CollapsibleTrigger asChild>
                                  <SidebarMenuButton
                                    asChild
                                    className={cn(
                                      "text-sidebar-foreground no-underline",
                                      pathname.startsWith(item.url) && state === "collapsed" && "bg-sidebar-accent text-sidebar-accent-foreground",
                                    )}
                                    forceTooltip={collapse}
                                    tooltip={item.title}
                                  >
                                    {state === "collapsed"
                                      ? (
                                          <Link href={item.url}>
                                            {item.icon && React.createElement(item.icon, { className: "size-4" })}
                                          </Link>
                                        )
                                      : (
                                          <div className="flex items-center gap-2">
                                            {item.icon && React.createElement(item.icon, { className: "size-4" })}
                                            <span>{item.title}</span>
                                            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                          </div>
                                        )}
                                  </SidebarMenuButton>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <SidebarMenuSub>
                                    {item.items.map(navItem => (
                                      <NavItem
                                        key={navItem.key}
                                        collapse={collapse}
                                        item={navItem}
                                        level={1}
                                        parseTitle={parseTitle}
                                        pathname={pathname}
                                      />
                                    ))}
                                  </SidebarMenuSub>
                                </CollapsibleContent>
                              </SidebarMenuItem>
                            </Collapsible>
                          )
                        : (
                            <SidebarMenuItem>
                              <SidebarMenuButton
                                asChild
                                className={cn(
                                  "text-sidebar-foreground no-underline",
                                  pathname.startsWith(item.url) && "bg-sidebar-accent text-sidebar-accent-foreground",
                                )}
                                forceTooltip={collapse}
                                tooltip={item.title}
                              >
                                <Link href={item.url}>
                                  {item.icon && React.createElement(item.icon, { className: "size-4" })}
                                  <span>{item.title}</span>
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          )}
                    </motion.div>
                  ),
            )}
          </SidebarMenu>
        </motion.div>
      </AnimatePresence>
    </SidebarGroup>
  )
}
